import React, { useState } from 'react';
import { Play, X } from 'lucide-react';

const videos = [
  {
    id: 1,
    title: "Why kidney stones cause extreme pain",
    duration: "3:45",
    thumbnail: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=400&h=250&fit=crop",
    youtubeId: "dQw4w9WgXcQ"
  },
  {
    id: 2,
    title: "When laser treatment is needed",
    duration: "5:20",
    thumbnail: "https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&h=250&fit=crop",
    youtubeId: "dQw4w9WgXcQ"
  },
  {
    id: 3,
    title: "How to prevent kidney stones again",
    duration: "4:15",
    thumbnail: "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=250&fit=crop",
    youtubeId: "dQw4w9WgXcQ"
  },
  {
    id: 4,
    title: "Diet tips for stone prevention",
    duration: "6:00",
    thumbnail: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=250&fit=crop",
    youtubeId: "dQw4w9WgXcQ"
  },
  {
    id: 5,
    title: "Understanding kidney stone types",
    duration: "4:30",
    thumbnail: "https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=400&h=250&fit=crop",
    youtubeId: "dQw4w9WgXcQ"
  },
  {
    id: 6,
    title: "Recovery after kidney stone surgery",
    duration: "3:55",
    thumbnail: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&h=250&fit=crop",
    youtubeId: "dQw4w9WgXcQ"
  }
];

const VideoCard = ({ video, onPlay }) => {
  return (
    <div 
      className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer group"
      onClick={() => onPlay(video)}
    >
      {/* Thumbnail Container */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        
        {/* Play Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-10 h-10 sm:w-14 sm:h-14 bg-teal-500 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
            <Play className="w-4 h-4 sm:w-6 sm:h-6 text-white fill-white ml-0.5 sm:ml-1" />
          </div>
        </div>

        {/* Duration Badge */}
        <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 bg-gray-900/80 text-white text-[10px] sm:text-xs font-medium px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md">
          {video.duration}
        </div>
      </div>

      {/* Title */}
      <div className="p-3 sm:p-4">
        <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors text-xs sm:text-base leading-tight">
          {video.title}
        </h3>
      </div>
    </div>
  );
};

const VideoModal = ({ video, onClose }) => {
  if (!video) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
        >
          <X className="w-8 h-8" />
        </button>

        {/* Video Player */}
        <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
          <iframe
            src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1`}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* Video Title */}
        <h3 className="text-white text-xl font-semibold mt-4">{video.title}</h3>
      </div>
    </div>
  );
};

const EducationalVideos = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 relative">
      {/* Overlapping badge from previous section */}
      <div className="absolute -top-6 sm:-top-10 left-1/2 -translate-x-1/2 z-10">
        <div className="bg-teal-500 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full shadow-lg shadow-teal-500/30 flex items-center gap-2">
          <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-white" />
          <span className="font-medium text-xs sm:text-sm">Watch & Learn</span>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 pt-4 sm:pt-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 italic">
            Kidney Educational Videos
          </h2>
          <p className="text-sm sm:text-lg text-gray-500">
            Learn from our expert specialists
          </p>
        </div>

        {/* Videos Grid - 2 rows of 3 */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {videos.map((video) => (
            <VideoCard 
              key={video.id} 
              video={video} 
              onPlay={setSelectedVideo}
            />
          ))}
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />
    </section>
  );
};

export default EducationalVideos;
